import {Checkbox} from '@fantasmagames/core/src/display/Checkbox';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {SpriteButton} from '@fantasmagames/core/src/display/SpriteButton';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {Timer} from '@fantasmagames/core/src/update/Timer';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {InfoViewEvents} from '@fantasmagames/slot/src/modules/hud/info/InfoViewEvents';
import {IRecodeVAPI} from '@fantasmagames/slot/src/integration/RecodeIntegrator';

export class InfoView extends View {
	public static readonly MULTILINES_COUNTER_DURATION: number = 250;

	protected _spineLayout: Spine;

	protected _topBar: PIXI.Sprite;
	protected _bottomBar: PIXI.Sprite;

	protected _betText: PIXI.Text;
	protected _winText: PIXI.Text;
	protected _balanceText: PIXI.Text;

	protected _betIcon: PIXI.Sprite;
	protected _winIcon: PIXI.Sprite;
	protected _balanceIcon: PIXI.Sprite;

	protected _freeBetText: PIXI.Text;

	protected _betValue: PIXI.Text;
	protected _winValue: PIXI.Text;
	protected _balanceValue: PIXI.Text;

	protected _clock: PIXI.Text;

	protected _soundButton: Checkbox;

	protected _corporationLogo: PIXI.Sprite;

	protected _textStyle: PIXI.TextStyle;
	protected _numberStyle: PIXI.TextStyle;
	protected _winFieldTextStyle: PIXI.TextStyle;
	protected _winFieldNumberStyle: PIXI.TextStyle;

	protected _timeUpdateIntervalId: number;
	protected _spineLayoutAnimation: SpineAnimation;

	protected _bankingButton: PIXI.Sprite;
	protected _vApi: IRecodeVAPI;

	public init(): void {
		super.init();

		this.setupLayout();
		this.setupTextStyles();
		this.setupValueObjects();
		this.setupGameObjects();
		this.device.isMobile ? this.setupIconObjects() : this.setupTextObjects();
		this.emitTextInitialized();
		this.setupSoundButton();
		this.startTimeInterval();

		this.setWins(0);
	}

	public translate(): void {
		if (!this._betText) {
			return;
		}

		const betText: string = this.translator.getObject('BaseStrings', 'generic', 'g_bet');
		this._betText.text = betText.toUpperCase();

		const winText: string = this.translator.getObject('BaseStrings', 'generic', 'g_win');
		this._winText.text = winText.toUpperCase();

		const balanceText: string = this.translator.getObject('BaseStrings', 'generic', 'g_balance');
		this._balanceText.text = balanceText.toUpperCase();
	}

	public setupFreeBetText(): void {
		const freeText: string = this.translator.getObject('BaseStrings', 'generic', 'g_freegames_free');

		this._freeBetText = new PIXI.Text(freeText.toUpperCase(), this._textStyle);

		this._freeBetText.transform = this._betValue.transform;
		this._betValue.parent.addChild(this._freeBetText);
		this._freeBetText.anchor.x = this._betValue.anchor.x;
		this._freeBetText.anchor.y = this._betValue.anchor.y;
	}

	public hideFreeBet(): void {
		if (this._freeBetText) {
			this._freeBetText.visible = false;
		}
	}

	public setFreeBet(): void {
		if (!this._freeBetText) {
			this.setupFreeBetText();
		} else {
			this._freeBetText.visible = true;
		}

		this.emitBetUpdated();
	}

	public setSoundButtonStatus(isOn: boolean, callCallback: boolean = false): void {
		this._soundButton.setValue(isOn, callCallback);
	}

	public playEntryAnimation(): void {
		this._spineLayoutAnimation.start();
		this.updateManager.add(this._spineLayoutAnimation);
		this.container.addChild(this._spineLayout);
	}

	public switchSound(enabled: boolean): void {
		enabled ? this.disableAllSound() : this.enableAllSound();
	}

	public hideBetValue(): void {
		this._betValue.visible = false;
	}

	public hideWinValue(): void {
		this._winValue.visible = false;
	}

	public hideBalanceValue(): void {
		this._balanceValue.visible = false;
	}

	public showBetValue(): void {
		this._betValue.visible = true;
	}

	public showWinValue(): void {
		this._winValue.visible = true;
	}

	public showBalanceValue(): void {
		this._balanceValue.visible = true;
	}

	public setBalance(newBalance: number): void {
		this._balanceValue.text = this.formatToCurrency(newBalance, true);
		this.emitBalanceUpdated();
	}

	public setBet(betValue: number): void {
		this._betValue.text = this.formatToCurrency(betValue, true);
		this.emitBetUpdated();
	}

	public setWins(winsValue: number): void {
		this._winValue.text = this.formatToCurrency(winsValue, true);
		this.emitWinUpdated();
	}

	protected setupGameObjects(): void {}

	protected emitWinUpdated(): void {
		this.emit(InfoViewEvents.WIN_UPDATED);
	}

	protected emitBalanceUpdated(): void {
		this.emit(InfoViewEvents.BALANCE_UPDATED);
	}

	protected emitBetUpdated(): void {
		this.emit(InfoViewEvents.BET_UPDATED);
	}

	protected emitTimeUpdated(): void {
		this.emit(InfoViewEvents.TIME_UPDATED);
	}

	protected emitAllSoundsToggleOff(): void {
		this.emit(InfoViewEvents.TOGGLE_ALL_SOUND_OFF);
	}

	protected emitAllSoundsToggleOn(): void {
		this.emit(InfoViewEvents.TOGGLE_ALL_SOUND_ON);
	}

	protected updateAll(): void {
		this.emitBetUpdated();
		this.emitBalanceUpdated();
		this.emitWinUpdated();
	}

	protected startTimeInterval(): void {
		this.updateTime();
	}

	protected stopTimeInterval(): void {
		if (this._timeUpdateIntervalId) {
			window.clearInterval(this._timeUpdateIntervalId);
		}
	}

	protected updateTime(): void {
		const time = new Date();
		const newTime = time.toLocaleTimeString(this.resources.getLocale());
		const character = newTime.lastIndexOf(':') === -1 ? '.' : ':';
		const newTimeText = newTime.substring(0, newTime.lastIndexOf(character));

		this._clock.text = newTimeText;

		const timer = new Timer(this.updateTime.bind(this), 30000, true);
		this.updateManager.add(timer);

		this.emitTimeUpdated();
	}

	protected formatToCurrency(value: number, useCurrencySign: boolean): string {
		return this.currencyFormatter.formatCurrency(value, useCurrencySign);
	}

	protected setTransform(
		baseobject: PIXI.DisplayObject,
		newTransformdata: PIXI.spine.core.BoneData | PIXI.spine.core.RegionAttachment,
		extraOffsetX: number = 0,
		extraOffsetY: number = 0,
	): void {
		const resolutionHeight = this.resolutionManager.resolution.height;
		const centerScreenX = this.resolutionManager.halfResolution.width;
		const centerScreenY = this.resolutionManager.halfResolution.height;
		const xPosition = centerScreenX + newTransformdata.x + extraOffsetX;
		const yPosition = resolutionHeight - (centerScreenY + newTransformdata.y + extraOffsetY);

		baseobject.setTransform(xPosition, yPosition, newTransformdata.scaleX, newTransformdata.scaleY);
	}

	protected disableAllSound(): void {
		this.emitAllSoundsToggleOff();
	}

	protected enableAllSound(): void {
		this.emitAllSoundsToggleOn();
	}

	protected setupLayout(): void {
		this._spineLayout = new Spine(this.resources.getSpine('GUI_INFO.json'));
		this._spineLayoutAnimation = new SpineAnimation(this._spineLayout, 'ENTRY', false, true);
		this._spineLayoutAnimation.update(0);
		this._spineLayout.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);
	}

	protected setupTextStyles(): void {
		const baseFontSize: number = this.device.isMobile ? 26 : 30;

		this._numberStyle = this.textStyles.guiInfoNumbers;
		this._textStyle = this.textStyles.guiInfoText;
		this._winFieldNumberStyle = this.textStyles.guiInfoWinFieldNumbers;
		this._winFieldTextStyle = this.textStyles.guiInfoWinFieldText;
	}

	protected setupIconObjects(): void {
		const betSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BET_TEXT')];
		const winSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('WIN_TEXT')];
		const balanceSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BALANCE_TEXT')];

		this._betIcon = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'BET_ICON.png'));
		this._winIcon = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'WIN_ICON.png'));
		this._balanceIcon = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'BALANCE_ICON.png'));

		this._betIcon.anchor.set(0.5, 0.5);
		this._winIcon.anchor.set(0.5, 0.5);
		this._balanceIcon.anchor.set(0.5, 0.5);

		betSlot.addChild(this._betIcon);
		winSlot.addChild(this._winIcon);
		balanceSlot.addChild(this._balanceIcon);

		this._betIcon.transform = betSlot.children[0].transform;
		this._winIcon.transform = winSlot.children[0].transform;
		this._balanceIcon.transform = balanceSlot.children[0].transform;

		betSlot.children[0].destroy();
		winSlot.children[0].destroy();
		balanceSlot.children[0].destroy();
	}

	protected setupValueObjects(): void {
		const betValueSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BET_NUMBER')];
		const winValueSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('WIN_NUMBER')];
		const balanceValueSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BALANCE_NUMBER')];

		const clockSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('CLOCK')];

		this._clock = new PIXI.Text('00:00', this._numberStyle);

		this._betValue = new PIXI.Text('', this._numberStyle);
		this._winValue = new PIXI.Text('', this._winFieldNumberStyle);
		this._balanceValue = new PIXI.Text('', this._numberStyle);

		betValueSlot.addChild(this._betValue);
		winValueSlot.addChild(this._winValue);
		balanceValueSlot.addChild(this._balanceValue);
		clockSlot.addChild(this._clock);

		this._betValue.transform = betValueSlot.children[0].transform;
		this._winValue.transform = winValueSlot.children[0].transform;
		this._balanceValue.transform = balanceValueSlot.children[0].transform;

		this._clock.transform = clockSlot.children[0].transform;

		betValueSlot.children[0].destroy();
		winValueSlot.children[0].destroy();
		balanceValueSlot.children[0].destroy();

		clockSlot.children[0].destroy();

		// this.container.addChild(this._clock);

		if (this.device.isMobile) {
			this._betValue.anchor.set(0.5, 0.5);
			this._winValue.anchor.set(0.5, 0.5);
			this._balanceValue.anchor.set(0.5, 0.5);
		} else {
			this._betValue.anchor.set(0, 0.5);
			this._winValue.anchor.set(0, 0.5);
			this._balanceValue.anchor.set(0, 0.5);
			const textXoffset = -10;
		}

		this._clock.anchor.set(0, 0.5);
		// this.setTransform(this._clock, clockData);
	}

	protected setupTextObjects(): void {
		const betTextSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BET_TEXT')];
		const winTextSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('WIN_TEXT')];
		const balanceTextSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('BALANCE_TEXT')];

		this._betText = new PIXI.Text('BET:', this._textStyle);
		this._winText = new PIXI.Text('WIN:', this._winFieldTextStyle);
		this._balanceText = new PIXI.Text('BALANCE:', this._textStyle);

		betTextSlot.addChild(this._betText);
		winTextSlot.addChild(this._winText);
		balanceTextSlot.addChild(this._balanceText);

		this._betText.transform = betTextSlot.children[0].transform;
		this._winText.transform = winTextSlot.children[0].transform;
		this._balanceText.transform = balanceTextSlot.children[0].transform;

		betTextSlot.children[0].destroy();
		winTextSlot.children[0].destroy();
		balanceTextSlot.children[0].destroy();

		if (this.device.isMobile) {
			this._betText.anchor.set(0.5, 0.5);
			this._winText.anchor.set(0.5, 0.5);
			this._balanceText.anchor.set(0.5, 0.5);
		} else {
			this._betText.anchor.set(1, 0.5);
			this._winText.anchor.set(1, 0.5);
			this._balanceText.anchor.set(1, 0.5);
			const textXoffset = -10;
		}
	}

	private emitTextInitialized(): void {
		this.emit(InfoViewEvents.TEXT_INITIALIZED);
	}

	private setupSoundButton(): void {
		const soundButtonSlot = this._spineLayout.slotContainers[this._spineLayout.skeleton.findSlotIndex('SOUND')];

		const soundOnButton: SpriteButton = new SpriteButton();
		const soundOnButtonImg: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'SOUND_OFF_ICON.png'));
		soundOnButtonImg.anchor.set(0.5, 0.5);
		soundOnButton.setFrames(soundOnButtonImg);

		const soundOffButton: SpriteButton = new SpriteButton();
		const soundOffButtonImg: PIXI.Sprite = new PIXI.Sprite(this.resources.getSpritesheetTexture('HUD.json', 'SOUND_ON_ICON.png'));
		soundOffButtonImg.anchor.set(0.5, 0.5);
		soundOffButton.setFrames(soundOffButtonImg);

		this._soundButton = new Checkbox(soundOnButton, soundOffButton, this.switchSound.bind(this));

		soundButtonSlot.addChild(soundOnButton);
		soundButtonSlot.addChild(soundOffButton);

		soundOnButton.transform = soundButtonSlot.children[0].transform;
		soundOffButton.transform = soundButtonSlot.children[0].transform;

		soundButtonSlot.children[0].destroy();
	}

	public get soundButton(): Checkbox {
		return this._soundButton;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/hud/info/InfoView.ts